'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink, PrismicNextImage } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';

export type FeatureHighlightWithCtaProps =
  SliceComponentProps<Content.FeatureHighlightWithCtaSlice>;

const FeatureHighlightWithCta: FC<FeatureHighlightWithCtaProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-[60px] pb-[80px] text-white"
      style={{ backgroundColor: '#0F423A' }}
    >
      <div className="max-w-[1200px] mx-auto px-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[100px] items-center">
          <div className="flex flex-col gap-6">
            {slice.primary.features.map((item, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl"
                style={{
                  borderRadius: '16px',
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)',
                  boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div className="flex items-center mb-2">
                  {item.icon.url && (
                    <div className="mr-3 w-6 h-6">
                      <PrismicNextImage
                        field={item.icon}
                        className="w-full h-full"
                      />
                    </div>
                  )}
                  <div className="feature-title">
                    <PrismicRichText
                      field={item.title}
                      components={{
                        heading4: ({ children }) => (
                          <h4 className="text-lg md:text-xl font-medium text-[#05f273] m-0 leading-[1.3]">
                            {children}
                          </h4>
                        ),
                        // Fallback for any other heading levels
                        heading1: ({ children }) => (
                          <h4 className="text-lg md:text-xl font-medium text-[#05f273] m-0 leading-[1.3]">
                            {children}
                          </h4>
                        ),
                        heading2: ({ children }) => (
                          <h4 className="text-lg md:text-xl font-medium text-[#05f273] m-0 leading-[1.3]">
                            {children}
                          </h4>
                        ),
                        heading3: ({ children }) => (
                          <h4 className="text-lg md:text-xl font-medium text-[#05f273] m-0 leading-[1.3]">
                            {children}
                          </h4>
                        ),
                      }}
                    />
                  </div>
                </div>
                <div className="text-base leading-6 text-[#c4c4c4]">
                  <PrismicRichText field={item.description} />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="mb-6">
              <PrismicRichText
                field={slice.primary.main_title}
                components={{
                  heading1: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
            <div className="text-lg leading-7 text-[#c4c4c4] mb-10">
              <PrismicRichText field={slice.primary.main_description} />
            </div>
            <PrismicNextLink
              field={slice.primary.cta_link}
              className="glassy-button inline-flex items-center justify-center py-4 px-8 rounded-full text-white font-semibold transition-all duration-300 relative overflow-hidden group"
            >
              <span>{slice.primary.button_label || 'Learn More'}</span>
              <ArrowRightIcon className="arrow-icon w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 group-hover:ml-2 transition-all duration-300" />
            </PrismicNextLink>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureHighlightWithCta;
